import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Maximize2, Minimize2 } from 'lucide-react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { getMeasuringBoxesMaps } from './core/_requests'
import { ID, QUERIES } from '../../../_metronic/helpers'
import { useAuth } from '../../modules/auth'
import { useAlarmedBoxes } from '../sensors-v2/hooks/useAlarmedBoxes'
import { maxSeverity } from '../../components/box-status-card/severity'
import { SensorTransportProvider } from '../sensors-v2/transports/SensorTransportProvider'
import { PageCard, SectionLabel, StatBlock, TOKENS } from '../../components/design-system'
import {
  MeasuringBoxModels,
  MeasuringBoxModelsList,
} from '../../modules/apps/measuring-box-management/measuring-box-list/core/_models'
import { EditModal } from '../../modules/apps/measuring-box-management/measuring-box-list/measuring-boxes-edit-modal/EditModal'
import { useListView } from '../../modules/apps/measuring-box-management/measuring-box-list/core/ListViewProvider'

type Status = 'high' | 'medium' | 'ok' | 'offline'

const STATUS_COLOR: Record<Status, string> = {
  high: TOKENS.color.severity.high.text,
  medium: TOKENS.color.severity.medium.text,
  ok: TOKENS.color.severity.low.text,
  offline: TOKENS.color.muted,
}

const STATUS_LABEL: Record<Status, string> = {
  high: 'Alta',
  medium: 'Média',
  ok: 'OK',
  offline: 'Offline',
}

// Box icon (matches lucide-react's Box) rendered inline so it can live
// inside a Leaflet DivIcon's HTML string. Color comes through via the
// `stroke` attribute set when we build the marker.
function boxSvg(color: string): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
         fill="none" stroke="${color}" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  `
}

// Token-driven Leaflet DivIcon — a rounded square holding the Box icon
// and the CMB number underneath. Color reflects the box's current alarm
// status. Replaces the plain colored circle that was the first cut.
function buildMarkerIcon(cmbNumber: number | string, status: Status): L.DivIcon {
  const color = STATUS_COLOR[status]
  const label = String(cmbNumber ?? '?')
  const html = `
    <div style="
      position:relative;
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      width:50px;
      font-family:inherit;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.18));
    ">
      <div style="
        width:34px;height:34px;
        background:#ffffff;border-radius:8px;
        border:2px solid ${color};
        display:flex;align-items:center;justify-content:center;
      ">
        ${boxSvg(color)}
      </div>
      <div style="
        margin-top:3px;
        background:${color};color:#fff;
        font-size:10px;font-weight:700;letter-spacing:0.2px;
        padding:1px 6px;border-radius:6px;
        line-height:1.3;
      ">${label}</div>
    </div>
  `
  return L.divIcon({
    html,
    className: '',
    iconSize: [50, 56],
    iconAnchor: [25, 28],
    popupAnchor: [0, -28],
  })
}

const Recenter: FC<{ position: [number, number] | null; zoom?: number }> = ({ position, zoom }) => {
  const map = useMap()
  useEffect(() => {
    if (position) map.setView(position, zoom ?? map.getZoom())
  }, [position, zoom, map])
  return null
}

const InvalidateOnResize: FC<{ trigger: unknown }> = ({ trigger }) => {
  const map = useMap()
  useEffect(() => {
    // After a layout change (fullscreen toggle, sidebar collapse) the map
    // tiles need to be refetched against the new container size.
    const t = setTimeout(() => map.invalidateSize(), 200)
    return () => clearTimeout(t)
  }, [trigger, map])
  return null
}

const LeafletMapInner: FC = () => {
  const { itemIdForUpdate, setItemIdForUpdate, setReadOnly } = useListView()
  const { currentUser } = useAuth()
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : 0

  const { data: measuringBoxesMaps, isFetching } = useQuery(
    // Bug fix: the previous key was static so changing the company in the
    // header didn't refresh the markers. Include company_id so each company
    // gets its own cache entry.
    [`${QUERIES.MEASURING_BOXES_LIST}-MAP`, companyId],
    async () => {
      const res = await getMeasuringBoxesMaps({ company_id: companyId, take: 0, skip: 10000 })
      return res.data?.filter((box) => box.lat !== null) ?? []
    },
    {
      enabled: companyId > 0,
      refetchOnWindowFocus: false,
      onError: (err: any) => {
        console.error(err)
        toast.error(`Erro ao carregar as caixas no mapa: ${err.message}`)
      },
    },
  )

  const { data: alarmedData } = useAlarmedBoxes({
    sort: 'gravity_desc',
    page: 1,
    items_per_page: 1000,
    company_id: companyId,
  })

  const severityByBoxId = useMemo(() => {
    const m = new Map<number, Status>()
    for (const b of alarmedData?.data ?? []) {
      if (!b.active_alarms.length) continue
      const sev = maxSeverity(b.active_alarms)
      m.set(Number(b.box_id), sev === 'high' ? 'high' : sev === 'medium' ? 'medium' : 'ok')
    }
    return m
  }, [alarmedData?.data])

  const boxesWithStatus = useMemo(() => {
    // Online rule:
    //   - WS box: last_seen_at within the last 5 min → 'ok', else 'offline'
    //     (the WS gateway writes last_seen_at on every app heartbeat)
    //   - HTTP polling box (firmware_protocol !== 'ws') or missing protocol:
    //     no reliable real-time signal, so treat as 'ok' by default
    //   - Any box with an active alarm wins severity ('high' / 'medium')
    //     over the online flag.
    const ONLINE_WINDOW_MS = 5 * 60 * 1000
    const now = Date.now()
    return (measuringBoxesMaps ?? []).map((box) => {
      const id = Number(box.box_id)
      const alarmed = severityByBoxId.get(id)
      if (alarmed) return { ...box, status: alarmed as Status }
      const proto = (box as any).firmware_protocol as 'ws' | 'http' | undefined
      const lastSeen = (box as any).last_seen_at as string | null | undefined
      let online: boolean
      if (proto === 'ws') {
        online = !!lastSeen && (now - new Date(lastSeen).getTime() < ONLINE_WINDOW_MS)
      } else {
        online = true
      }
      const status: Status = online ? 'ok' : 'offline'
      return { ...box, status }
    })
  }, [measuringBoxesMaps, severityByBoxId])

  const totalsByStatus = useMemo(() => {
    const t: Record<Status, number> = { high: 0, medium: 0, ok: 0, offline: 0 }
    for (const b of boxesWithStatus) t[b.status] += 1
    return t
  }, [boxesWithStatus])

  // Side-panel state — search + status filter. Default filter is "Todas"
  // so the operator sees every box of the company at a glance; the
  // "Alarmadas" chip is one tap away for triage.
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'alarmed' | 'all' | Status>('all')

  const STATUS_PRIORITY: Record<Status, number> = { high: 0, medium: 1, ok: 2, offline: 3 }

  const filteredList = useMemo(() => {
    const term = search.trim().toLowerCase()
    return boxesWithStatus
      .filter((b) => {
        if (statusFilter === 'alarmed' && !(b.status === 'high' || b.status === 'medium')) return false
        if (statusFilter !== 'alarmed' && statusFilter !== 'all' && b.status !== statusFilter) return false
        if (term) {
          const cmb = String(b.cmb_number ?? '').toLowerCase()
          const bt = String(b.bluetooth ?? '').toLowerCase()
          if (!cmb.includes(term) && !bt.includes(term)) return false
        }
        return true
      })
      .sort((a, b) => STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxesWithStatus, search, statusFilter])

  const defaultCenter = useMemo<[number, number] | null>(() => {
    if (!boxesWithStatus.length) return null
    const first = boxesWithStatus[0]
    return [parseFloat(String(first.lat)), parseFloat(String(first.long))]
  }, [boxesWithStatus])

  const fallbackCenter: [number, number] = [-15.78, -47.92]
  const [focusedCenter, setFocusedCenter] = useState<[number, number] | null>(null)
  const [activePopup, setActivePopup] = useState<number | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const markerRefs = useRef(new Map<number, L.Marker>())

  const handleSidebarClick = (boxId: number, lat: number, lng: number) => {
    setFocusedCenter([lat, lng])
    setActivePopup(boxId)
    setTimeout(() => markerRefs.current.get(boxId)?.openPopup(), 100)
  }

  if (isFetching && !measuringBoxesMaps?.length) {
    return (
      <div style={{ padding: TOKENS.spacing.lg, display: 'flex', alignItems: 'center', gap: TOKENS.spacing.sm }}>
        <span className='spinner-border spinner-border-sm' />
        <span style={{ fontSize: 14, color: TOKENS.color.muted }}>Carregando o mapa…</span>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: fullscreen ? 0 : TOKENS.spacing.md,
        height: 'calc(100vh - 180px)',
        minHeight: 480,
      }}
    >
      {!fullscreen && (
        <aside style={{ width: 280, display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md, flexShrink: 0 }}>
          <PageCard>
            <SectionLabel>Status das caixas</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: TOKENS.spacing.sm, marginTop: TOKENS.spacing.md }}>
              <StatBlock value={totalsByStatus.high} label='Alta' tone='high' size='sm' />
              <StatBlock value={totalsByStatus.medium} label='Média' tone='medium' size='sm' />
              <StatBlock value={totalsByStatus.ok} label='OK' tone='low' size='sm' />
              <StatBlock value={totalsByStatus.offline} label='Offline' tone='neutral' size='sm' />
            </div>
          </PageCard>

          <PageCard style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <SectionLabel>
              Caixas {filteredList.length !== boxesWithStatus.length && <span style={{ color: TOKENS.color.text, fontWeight: 700 }}>({filteredList.length})</span>}
            </SectionLabel>

            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='🔍 Buscar CMB ou Bluetooth'
              style={{
                marginTop: TOKENS.spacing.sm,
                border: `1px solid ${TOKENS.color.border}`,
                borderRadius: TOKENS.radius.chip,
                padding: '6px 10px',
                fontSize: 12,
                outline: 'none',
                color: TOKENS.color.text,
              }}
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: TOKENS.spacing.sm }}>
              {([
                { key: 'alarmed', label: 'Alarmadas' },
                { key: 'high', label: 'Alta' },
                { key: 'medium', label: 'Média' },
                { key: 'ok', label: 'OK' },
                { key: 'offline', label: 'Offline' },
                { key: 'all', label: 'Todas' },
              ] as { key: typeof statusFilter; label: string }[]).map((opt) => {
                const active = statusFilter === opt.key
                return (
                  <span
                    key={opt.key}
                    role='button'
                    onClick={() => setStatusFilter(opt.key)}
                    style={{
                      padding: '3px 9px',
                      borderRadius: TOKENS.radius.chip,
                      background: active ? TOKENS.color.action : TOKENS.color.surface,
                      color: active ? '#fff' : TOKENS.color.text,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </span>
                )
              })}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginTop: TOKENS.spacing.sm, marginRight: -8, paddingRight: 8 }}>
              {filteredList.length === 0 ? (
                <div style={{ fontSize: 12, color: TOKENS.color.muted, padding: '12px 0', textAlign: 'center' }}>
                  Nenhuma caixa encontrada.
                </div>
              ) : (
                filteredList.map((b) => {
                  const lat = parseFloat(String(b.lat))
                  const lng = parseFloat(String(b.long))
                  return (
                    <div
                      key={String(b.box_id)}
                      role='button'
                      onClick={() => handleSidebarClick(Number(b.box_id), lat, lng)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        marginBottom: 4,
                        borderRadius: TOKENS.radius.chip,
                        background: TOKENS.color.surface,
                        cursor: 'pointer',
                        borderLeft: `3px solid ${STATUS_COLOR[b.status]}`,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, color: TOKENS.color.text }}>
                        CMB {b.cmb_number}
                      </span>
                      <span style={{
                        background: STATUS_COLOR[b.status],
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: TOKENS.radius.chip,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                      }}>
                        {STATUS_LABEL[b.status]}
                      </span>
                    </div>
                  )
                })
              )}
            </div>
          </PageCard>
        </aside>
      )}

      <div
        style={{
          position: 'relative',
          flex: 1,
          borderRadius: TOKENS.radius.card,
          overflow: 'hidden',
          border: `1px solid ${TOKENS.color.border}`,
          minHeight: 0,
        }}
      >
        <MapContainer
          center={focusedCenter ?? defaultCenter ?? fallbackCenter}
          zoom={focusedCenter || defaultCenter ? 15 : 4}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <LayersControl position='topleft'>
            <LayersControl.BaseLayer checked name='Mapa'>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='Satélite'>
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='Híbrido'>
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                maxZoom={19}
              />
              <TileLayer
                attribution='Reference &copy; Esri'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
                maxZoom={19}
                opacity={0.9}
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          <Recenter position={focusedCenter} zoom={16} />
          <InvalidateOnResize trigger={fullscreen} />
          {boxesWithStatus.map((box) => {
            const lat = parseFloat(String(box.lat))
            const lng = parseFloat(String(box.long))
            if (Number.isNaN(lat) || Number.isNaN(lng)) return null
            const boxId = Number(box.box_id)
            return (
              <Marker
                key={String(box.box_id)}
                position={[lat, lng]}
                icon={buildMarkerIcon(box.cmb_number, box.status)}
                eventHandlers={{ click: () => setActivePopup(boxId) }}
                ref={(m) => { if (m) markerRefs.current.set(boxId, m as unknown as L.Marker) }}
              >
                {activePopup === boxId && (
                  <Popup
                    position={[lat, lng]}
                    eventHandlers={{ remove: () => setActivePopup(null) }}
                  >
                    <BoxPopup
                      box={{
                        id: Number(box.box_id) as unknown as ID,
                        cmb_number: box.cmb_number,
                        bluetooth: box.bluetooth,
                        project_name: box.project_name,
                        model_type: box.model_type,
                        lat,
                        lng,
                        status: box.status,
                      }}
                      onConsult={() => {
                        setReadOnly && setReadOnly(true)
                        setItemIdForUpdate(Number(box.box_id) as unknown as ID)
                      }}
                    />
                  </Popup>
                )}
              </Marker>
            )
          })}
        </MapContainer>
        <button
          type='button'
          onClick={() => setFullscreen((v) => !v)}
          title={fullscreen ? 'Recolher mapa' : 'Ampliar mapa'}
          style={{
            position: 'absolute',
            top: TOKENS.spacing.md,
            right: TOKENS.spacing.md,
            zIndex: 1000,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: TOKENS.color.card,
            border: `1px solid ${TOKENS.color.border}`,
            borderRadius: TOKENS.radius.card,
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 600,
            color: TOKENS.color.text,
            cursor: 'pointer',
            boxShadow: TOKENS.shadow.card,
          }}
        >
          {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          <span>{fullscreen ? 'Recolher' : 'Ampliar'}</span>
        </button>
      </div>
      {itemIdForUpdate !== undefined && <EditModal />}
    </div>
  )
}

type PopupBox = {
  id: ID
  cmb_number: number
  bluetooth: string
  project_name: string
  model_type: number
  lat: number
  lng: number
  status: Status
}

const BoxPopup: FC<{ box: PopupBox; onConsult: () => void }> = ({ box, onConsult }) => {
  const color = STATUS_COLOR[box.status]
  const modelLabel = MeasuringBoxModelsList.find((m) => m.id === box.model_type)?.name
    ?? (box.model_type === MeasuringBoxModels.bluetooth_com_barreira
      ? 'Bluetooth com barreira'
      : box.model_type === MeasuringBoxModels.bluetooth_sem_barreira
      ? 'Bluetooth sem barreira'
      : 'Não identificado')
  return (
    <div style={{ minWidth: 220, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: TOKENS.spacing.sm, marginBottom: TOKENS.spacing.md }}>
        <span style={{
          background: color, color: '#fff',
          padding: '2px 8px', borderRadius: TOKENS.radius.chip,
          fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
        }}>
          {STATUS_LABEL[box.status]}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text }}>
          CMB {box.cmb_number}
        </span>
      </div>
      <div style={{ fontSize: 12, color: TOKENS.color.muted, lineHeight: 1.6 }}>
        <div><strong style={{ color: TOKENS.color.text }}>Projeto:</strong> {box.project_name || '—'}</div>
        <div><strong style={{ color: TOKENS.color.text }}>Bluetooth:</strong> {box.bluetooth}</div>
        <div><strong style={{ color: TOKENS.color.text }}>Modelo:</strong> {modelLabel}</div>
        <div style={{ marginTop: 4 }}>
          {box.lat.toFixed(6)}, {box.lng.toFixed(6)}
        </div>
      </div>
      <button
        type='button'
        onClick={onConsult}
        style={{
          marginTop: TOKENS.spacing.md,
          width: '100%',
          border: 'none',
          background: TOKENS.color.action,
          color: '#fff',
          padding: '8px 12px',
          borderRadius: TOKENS.radius.chip,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Consultar detalhes
      </button>
    </div>
  )
}

export const LeafletMap: FC = () => (
  <SensorTransportProvider transport='websocket'>
    <LeafletMapInner />
  </SensorTransportProvider>
)
